// AuxiliaireDashboard.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  doc, getDoc, collection, query, where, getDocs,
  addDoc, updateDoc, Timestamp, orderBy
} from "firebase/firestore";
import { db } from "../components/firebase-config.js";
import {
  Container, Row, Col, Card, Button, Form, Alert,
  Tabs, Tab, ListGroup, Badge, Spinner
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus, faCalendarPlus, faSignOutAlt,
  faUserMd, faCalendarDay, faCheck, faTimes,
  faSpinner, faExclamationTriangle, faClock,
  faUser, faEnvelope, faPhone, faCalendarAlt,
  faClipboardList, faBell, faSearch, faBars,
  faChevronLeft, faEllipsisV, faInfoCircle,faQrcode
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import QRCodeGenerator  from './QRCodeGenerator.js'

// Animations
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem 0;
  
  @media (max-width: 768px) {
    padding: 0;
    background: #ffffff;
  }
`;

const DashboardHeader = styled.div`
  background: white;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.05);
  padding: 1.5rem 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 768px) {
    border-radius: 0;
    padding: 1rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 0;
    z-index: 100;
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #4b5563;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 80%;
  height: 100vh;
  background: white;
  z-index: 1000;
  box-shadow: 5px 0 15px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const MobileMenuItem = styled(motion.div)`
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  
  &.active {
    background: linear-gradient(-45deg, #6366f1, #8b5cf6, #3b82f6);
    background-size: 200% 200%;
    animation: ${gradientShift} 5s ease infinite;
    color: white;
  }
  
  svg {
    margin-right: 1rem;
  }
`;

const WelcomeBanner = styled(motion.div)`
  background: linear-gradient(-45deg, #6366f1, #8b5cf6, #3b82f6, #06b6d4);
  background-size: 400% 400%;
  animation: ${gradientShift} 15s ease infinite;
  color: white;
  border-radius: 24px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 25px rgba(99, 102, 241, 0.2);
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    background-size: 200% 100%;
    animation: ${shimmerAnimation} 8s infinite;
  }
  
  @media (max-width: 768px) {
    border-radius: 0;
    margin: -1rem -1rem 1.5rem -1rem;
    padding: 1.5rem;
  }
`;

const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  margin-bottom: 2rem;
  
  &:hover {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    border-radius: 16px;
    margin-bottom: 1rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    
    &:hover {
      transform: none;
    }
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to right, #f8f9fa, white);
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    
    h5 {
      margin-bottom: 0.5rem;
    }
  }
`;

const CardBody = styled.div`
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StyledButton = styled(motion.button)`
  border-radius: 16px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  
  &.primary {
    background: linear-gradient(-45deg, #6366f1, #8b5cf6, #3b82f6);
    background-size: 200% 200%;
    animation: ${gradientShift} 5s ease infinite;
    color: white;
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.2);
    
    &:hover {
      box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
      transform: translateY(-2px);
    }
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #4b5563;
    
    &:hover {
      background: #e9ecef;
      transform: translateY(-2px);
    }
  }
  
  &.danger {
    background: linear-gradient(-45deg, #ef4444, #dc2626, #b91c1c);
    background-size: 200% 200%;
    animation: ${gradientShift} 5s ease infinite;
    color: white;
    
    &:hover {
      box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
      transform: translateY(-2px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
    border-radius: 12px;
    
    &:hover {
      transform: none;
    }
  }
  
  &.mobile-full {
    @media (max-width: 768px) {
      width: 100%;
      justify-content: center;
    }
  }
`;

const FloatingActionButton = styled(motion.button)`
  display: none;
  position: fixed;
  bottom: 80px; // Augmenter cette valeur pour le placer au-dessus de la barre de navigation
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(-45deg, #6366f1, #8b5cf6, #3b82f6);
  background-size: 200% 200%;
  animation: ${gradientShift} 5s ease infinite;
  color: white;
  border: none;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.3);
  font-size: 1.5rem;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TimeSlot = styled(motion.div)`
  padding: 1rem;
  border-radius: 16px;
  text-align: center;
  cursor: pointer;
  position: relative;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &.disponible {
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    color: #059669;
    border: 1px solid #a7f3d0;
  }
  
  &.partiel {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    color: #d97706;
    border: 1px solid #fcd34d;
  }
  
  &.complet {
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    color: #6b7280;
    border: 1px solid #e5e7eb;
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  &.selected {
    border: 2px solid #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
    animation: ${pulseAnimation} 2s infinite;
  }
  
  .slot-info {
    font-size: 0.8rem;
    margin-top: 0.5rem;
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    border-radius: 12px;
    font-size: 0.9rem;
    
    .slot-info {
      font-size: 0.7rem;
    }
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  
  th {
    background-color: #f8f9fa;
    color: #4b5563;
    font-weight: 600;
    padding: 1rem 1.5rem;
    text-align: left;
    border-bottom: 2px solid #e5e7eb;
  }
  
  td {
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: middle;
  }
  
  tr {
    transition: all 0.3s ease;
    
    &:hover {
      background-color: #f8f9fa;
    }
    
    &.status-confirmé {
      border-left: 4px solid #059669;
    }
    
    &.status-annulé {
      border-left: 4px solid #dc2626;
      opacity: 0.7;
    }
    
    &.status-terminé {
      border-left: 4px solid #0284c7;
    }
  }
  
  @media (max-width: 768px) {
    display: block;
    
    thead, tbody, th, td, tr {
      display: block;
    }
    
    thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }
    
    tr {
      margin-bottom: 1rem;
      border-radius: 12px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
      overflow: hidden;
      background: white;
      position: relative;
      
      &.status-confirmé {
        border-left: none;
        border-top: 4px solid #059669;
      }
      
      &.status-annulé {
        border-left: none;
        border-top: 4px solid #dc2626;
      }
      
      &.status-terminé {
        border-left: none;
        border-top: 4px solid #0284c7;
      }
    }
    
    td {
      padding: 0.75rem 1rem;
      text-align: right;
      position: relative;
      padding-left: 50%;
      border-bottom: 1px solid #f3f4f6;
      
      &:before {
        position: absolute;
        top: 0.75rem;
        left: 1rem;
        width: 45%;
        padding-right: 10px;
        white-space: nowrap;
        font-weight: 600;
        color: #6b7280;
        text-align: left;
      }
      
      &:nth-of-type(1):before { content: "Heure"; }
      &:nth-of-type(2):before { content: "Médecin"; }
      &:nth-of-type(3):before { content: "Patient"; }
      &:nth-of-type(4):before { content: "Motif"; }
      &:nth-of-type(5):before { content: "Statut"; }
    }
  }
`;

const MobileCardList = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
  overflow: hidden;
  
  &.status-confirmé {
    border-left: 4px solid #059669;
  }
  
  &.status-annulé {
    border-left: 4px solid #dc2626;
    opacity: 0.8;
  }
  
  &.status-terminé {
    border-left: 4px solid #0284c7;
  }
`;

const MobileCardHeader = styled.div`
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
  background: #f9fafb;
`;

const MobileCardBody = styled.div`
  padding: 1rem;
`;

const MobileCardItem = styled.div`
  display: flex;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .label {
    width: 40%;
    font-weight: 600;
    color: #6b7280;
    font-size: 0.9rem;
  }
  
  .value {
    width: 60%;
    font-size: 0.9rem;
  }
`;

const MobilePatientCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
  overflow: hidden;
  padding: 1rem;
`;

const MobilePatientHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const MobilePatientDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobilePatientContact = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  
  svg {
    margin-right: 0.5rem;
    color: #6b7280;
  }
`;

const StatusBadge = styled.span`
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &.status-badge-confirmé {
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    color: #059669;
  }
  
  &.status-badge-annulé {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    color: #dc2626;
  }
  
  &.status-badge-terminé {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    color: #0284c7;
  }
  
  @media (max-width: 768px) {
    padding: 0.35rem 0.75rem;
    font-size: 0.7rem;
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 16px;
  padding: 0.75rem 1.25rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  
  &:focus-within {
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.15);
    border-color: #8b5cf6;
  }
  
  input {
    border: none;
    outline: none;
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 1rem;
    color: #4b5563;
    
    &::placeholder {
      color: #9ca3af;
    }
  }
  
  svg {
    color: #6b7280;
    margin-right: 0.75rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    border-radius: 12px;
    margin-bottom: 1rem;
    
    input {
      padding: 0.3rem 0.5rem;
      font-size: 0.9rem;
    }
  }
`;

const StyledTabs = styled(Tabs)`
  border-bottom: none;
  margin-bottom: 2rem;
  
  .nav-link {
    border: none;
    color: #6b7280;
    padding: 1rem 1.5rem;
    border-radius: 16px;
    margin-right: 0.75rem;
    font-weight: 600;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: #f3f4f6;
      color: #4b5563;
    }
    
    &.active {
      background: linear-gradient(-45deg, #6366f1, #8b5cf6, #3b82f6);
      background-size: 200% 200%;
      animation: ${gradientShift} 5s ease infinite;
      color: white;
      box-shadow: 0 4px 15px rgba(99, 102, 241, 0.2);
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    
    .nav-link {
      padding: 0.75rem 1rem;
      border-radius: 12px;
      margin-right: 0.5rem;
      font-size: 0.9rem;
    }
  }
  
  @media (max-width: 480px) {
    display: flex;
    
    .nav-item {
      flex: 1;
    }
    
    .nav-link {
      text-align: center;
      padding: 0.75rem 0.5rem;
      margin-right: 0.5rem;
      
      .badge {
        display: none;
      }
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  
  svg {
    font-size: 3.5rem;
    color: #d1d5db;
    margin-bottom: 1.5rem;
    opacity: 0.7;
  }
  
  h5 {
    color: #6b7280;
    margin-bottom: 1.5rem;
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    
    svg {
      font-size: 2.5rem;
      margin-bottom: 1rem;
    }
    
    h5 {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
  }
`;

const NotificationBell = styled(motion.div)`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 10px;
    height: 10px;
    background-color: #ef4444;
    border-radius: 50%;
    border: 2px solid white;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    
    &::after {
      width: 8px;
      height: 8px;
    }
  }
`;

const IconWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e0f2fe 0%, #bfdbfe 100%);
  color: #3b82f6;
  margin-right: 12px;
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    margin-right: 10px;
    font-size: 0.9rem;
  }
`;

const MobileTabBar = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: white;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
    z-index: 100;
  }
`;

const MobileTabItem = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0;
  color: #6b7280;
  cursor: pointer;
  
  &.active {
    color: #6366f1;
  }
  
  svg {
    font-size: 1.25rem;
    margin-bottom: 0.25rem;
  }
  
  span {
    font-size: 0.75rem;
    font-weight: 500;
  }
`;

// Nouveau composant pour l'alerte d'horaire de travail
const HoraireAlert = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  
  .icon-container {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
    color: #dc2626;
    font-size: 3rem;
  }
  
  h2 {
    color: #dc2626;
    margin-bottom: 1rem;
  }
  
  p {
    max-width: 500px;
    margin-bottom: 2rem;
    font-size: 1.1rem;
    color: #4b5563;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    
    .icon-container {
      width: 100px;
      height: 100px;
      font-size: 2.5rem;
      margin-bottom: 1.5rem;
    }
    
    h2 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      duration: 0.5,
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { duration: 0.3 }
  }
};

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.3 }
  }
};

const mobileMenuVariants = {
  closed: {
    x: "-100%",
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 40
    }
  },
  open: {
    x: 0,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 40
    }
  }
};

const overlayVariants = {
  closed: {
    opacity: 0,
    transition: {
      delay: 0.2
    }
  },
  open: {
    opacity: 1
  }
};

const AuxiliaireDashboard = () => {
  const [auxiliaireData, setAuxiliaireData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [medecins, setMedecins] = useState([]);
  const [patients, setPatients] = useState([]);
  const [rendezvous, setRendezvous] = useState([]);
  const [activeTab, setActiveTab] = useState("rendezVous");
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // État pour le compte à rebours de déconnexion
const [countdownSeconds, setCountdownSeconds] = useState(null);

  // États pour l'ajout de patient
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [patientForm, setPatientForm] = useState({
    nom: "",
    dateNaissance: "",
    email: "",
    telephone: "",
    assurances: []
  });
  
  // États pour l'ajout de rendez-vous
  const [showRendezVousForm, setShowRendezVousForm] = useState(false);
  const [rdvForm, setRdvForm] = useState({
    medecinId: "",
    patientId: "",
    heure: "",
    duree: 15,
    motif: "",
    statut: "confirmé"
  });
  
  // Créneaux disponibles pour aujourd'hui
  const [creneauxDisponibles, setCreneauxDisponibles] = useState([]);
  
  // États pour la vérification des horaires de travail
  const [horsHoraireTravail, setHorsHoraireTravail] = useState(false);
  const [horaireInfo, setHoraireInfo] = useState({
    jour: "",
    debut: "",
    fin: ""
  });
  
  const navigate = useNavigate();
  const auth = getAuth();
  
  // Date d'aujourd'hui au format YYYY-MM-DD
  const today = new Date().toISOString().split("T")[0];
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Vérifier si l'utilisateur est un auxiliaire
          const docRef = doc(db, "auxiliaires", user.uid);
          
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            setAuxiliaireData(data);
            
            // Récupérer les médecins assignés à cet auxiliaire
            if (data.medecinIds && data.medecinIds.length > 0) {
              const medecinsData = [];
              for (const medecinId of data.medecinIds) {
                const medecinDoc = await getDoc(doc(db, "medecins", medecinId));
                if (medecinDoc.exists()) {
                  medecinsData.push({ id: medecinId, ...medecinDoc.data() });
                }
              }
              setMedecins(medecinsData);
            }
            
            // Récupérer les patients de la structure
            if (data.structureId) {
              const patientsQuery = query(
                collection(db, "patients"),
                where("structureId", "==", data.structureId)
              );
              const patientsSnapshot = await getDocs(patientsQuery);
              const patientsData = [];
              patientsSnapshot.forEach((doc) => {
                patientsData.push({ id: doc.id, ...doc.data() });
              });
              setPatients(patientsData);
              
              // Récupérer les rendez-vous d'aujourd'hui pour les médecins assignés
              if (data.medecinIds && data.medecinIds.length > 0) {
                const rdvQuery = query(
                  collection(db, "rendezvous"),
                  where("structureId", "==", data.structureId),
                  where("date", "==", today),
                  where("medecinId", "in", data.medecinIds)
                );
                const rdvSnapshot = await getDocs(rdvQuery);
                const rdvData = [];
                rdvSnapshot.forEach((doc) => {
                  rdvData.push({ id: doc.id, ...doc.data() });
                });
                setRendezvous(rdvData);
              }
            }
            
            setLoading(false);
          } else {
            // L'utilisateur n'est pas un auxiliaire
            setError("Vous n'êtes pas autorisé à accéder à cette page.");
            setLoading(false);
            setTimeout(() => {
              signOut(auth);
              navigate("/auth");
            }, 3000);
          }
        } catch (err) {
          setError(`Erreur: ${err.message}`);
          setLoading(false);
        }
      } else {
        navigate("/auth");
      }
    });
    
    return () => unsubscribe();
  }, [auth, navigate]);
  
  // Fonction pour vérifier les créneaux disponibles pour un médecin aujourd'hui
  const checkCreneauxDisponibles = (medecinId) => {
    if (!medecinId) return [];
    
    const medecin = medecins.find(m => m.id === medecinId);
    if (!medecin) return [];
    
    // Déterminer le jour de la semaine
    const jourDate = new Date();
    const joursMap = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
    const jourSemaine = joursMap[jourDate.getDay()];
    
    // Vérifier si le médecin travaille aujourd'hui
    let heureDebut = "";
    let heureFin = "";
    
    if (Array.isArray(medecin.disponibilites)) {
      const disponibiliteJour = medecin.disponibilites.find(
        d => d.jour.toLowerCase() === jourSemaine.toLowerCase()
      );
      
      if (disponibiliteJour) {
        heureDebut = disponibiliteJour.heureDebut;
        heureFin = disponibiliteJour.heureFin;
      } else {
        return []; // Le médecin ne travaille pas aujourd'hui
      }
    } else if (medecin.disponibilites && medecin.disponibilites[jourSemaine]) {
      if (!medecin.disponibilites[jourSemaine].actif) {
        return []; // Le médecin ne travaille pas aujourd'hui
      }
      heureDebut = medecin.disponibilites[jourSemaine].debut;
      heureFin = medecin.disponibilites[jourSemaine].fin;
    } else {
      return []; // Pas de disponibilités pour aujourd'hui
    }
    
    // Récupérer les rendez-vous existants pour ce médecin aujourd'hui
    const rdvDuJour = rendezvous.filter(
      rdv => rdv.medecinId === medecinId && rdv.date === today && rdv.statut !== "annulé"
    );
    
    // Générer des créneaux de 15 minutes
    const creneaux = [];
    let heure = heureDebut;
    
    // Nombre maximum de rendez-vous simultanés autorisés
    const MAX_RDV_SIMULTANES = 3;
    
    while (heure < heureFin) {
      const [h, m] = heure.split(":").map(Number);
      const heureFinCreneau = new Date();
      heureFinCreneau.setHours(h, m + parseInt(rdvForm.duree), 0, 0);
      const heureFinStr = `${heureFinCreneau.getHours().toString().padStart(2, "0")}:${heureFinCreneau.getMinutes().toString().padStart(2, "0")}`;
      
      if (heureFinStr <= heureFin) {
        // Compter le nombre de rendez-vous à cette heure
        const rdvAuCreneau = rdvDuJour.filter(rdv => rdv.heure === heure);
        const nombreRdvAuCreneau = rdvAuCreneau.length;
        
        // Déterminer le statut du créneau
        let statut = "disponible";
        if (nombreRdvAuCreneau >= MAX_RDV_SIMULTANES) {
          statut = "complet";
        } else if (nombreRdvAuCreneau > 0) {
          statut = "partiel";
        }
        
        // Vérifier si ce créneau est déjà passé aujourd'hui
        const maintenant = new Date();
        const heureActuelle = maintenant.getHours();
        const minuteActuelle = maintenant.getMinutes();
        
        const estPasse = (heureActuelle > h) || (heureActuelle === h && minuteActuelle > m);
        
        if (!estPasse) {
          creneaux.push({
            heure: heure,
            statut: statut,
            nombreRdv: nombreRdvAuCreneau,
            estDisponible: statut !== "complet"
          });
        }
      }
      
      // Passer au créneau suivant (par tranches de 15 minutes)
      const nextHeure = new Date();
      nextHeure.setHours(h, m + 15, 0, 0);
      heure = `${nextHeure.getHours().toString().padStart(2, "0")}:${nextHeure.getMinutes().toString().padStart(2, "0")}`;
    }
    
    return creneaux;
  };
  
  // Fonction pour gérer le changement de médecin dans le formulaire de rendez-vous
  const handleMedecinChange = (medecinId) => {
    setRdvForm({
      ...rdvForm,
      medecinId,
      heure: "" // Réinitialiser l'heure sélectionnée
    });
    
    // Mettre à jour les créneaux disponibles
    const creneaux = checkCreneauxDisponibles(medecinId);
    setCreneauxDisponibles(creneaux);
  };
  
  // Fonction pour gérer les changements dans le formulaire de patient
  const handlePatientChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "assurances") {
      const updatedArray = [...patientForm.assurances];
      if (checked) {
        updatedArray.push(value);
      } else {
        const index = updatedArray.indexOf(value);
        if (index > -1) {
          updatedArray.splice(index, 1);
        }
      }
      setPatientForm({ ...patientForm, [name]: updatedArray });
    } else {
      setPatientForm({ ...patientForm, [name]: value });
    }
  };
  
  // Fonction pour réinitialiser le formulaire de patient
  const resetPatientForm = () => {
    setPatientForm({
      nom: "",
      dateNaissance: "",
      email: "",
      telephone: "",
      assurances: []
    });
  };
  
  // Fonction pour ajouter un patient
  const handleAddPatient = async (e) => {
    e.preventDefault();
    try {
      if (!auxiliaireData.permissions.ajouterPatients) {
        setMessage("Vous n'avez pas la permission d'ajouter des patients.");
        return;
      }
      
      setLoading(true);
      
      const patientData = {
        ...patientForm,
        structureId: auxiliaireData.structureId,
        createdAt: new Date(),
        createdBy: {
          id: auth.currentUser.uid,
          type: "auxiliaire",
          nom: `${auxiliaireData.prenom} ${auxiliaireData.nom}`
        }
      };
      
      const docRef = await addDoc(collection(db, "patients"), patientData);
      
      // Ajouter le nouveau patient à la liste locale
      setPatients([...patients, { id: docRef.id, ...patientData }]);
      
      setMessage("Patient ajouté avec succès.");
      resetPatientForm();
      setShowPatientForm(false);
      setLoading(false);
      
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(`Erreur: ${error.message}`);
      setLoading(false);
    }
  };
  
  // Fonction pour gérer les changements dans le formulaire de rendez-vous
  const handleRdvChange = (e) => {
    const { name, value } = e.target;
    setRdvForm({ ...rdvForm, [name]: value });
  };
  
  // Fonction pour réinitialiser le formulaire de rendez-vous
  const resetRdvForm = () => {
    setRdvForm({
      medecinId: "",
      patientId: "",
      heure: "",
      duree: 15,
      motif: "",
      statut: "confirmé"
    });
    setCreneauxDisponibles([]);
  };
  
  // Fonction pour ajouter un rendez-vous
  const handleAddRendezVous = async (e) => {
    e.preventDefault();
    try {
      if (!auxiliaireData.permissions.ajouterRendezVous) {
        setMessage("Vous n'avez pas la permission d'ajouter des rendez-vous.");
        return;
      }
      
      setLoading(true);
      
      // Vérifier que le créneau est toujours disponible
      const creneaux = checkCreneauxDisponibles(rdvForm.medecinId);
      const creneauSelectionne = creneaux.find(c => c.heure === rdvForm.heure);
      
      if (!creneauSelectionne || !creneauSelectionne.estDisponible) {
        setMessage("Ce créneau n'est plus disponible. Veuillez en choisir un autre.");
        setLoading(false);
        return;
      }
      
      const medecin = medecins.find(m => m.id === rdvForm.medecinId);
      const patient = patients.find(p => p.id === rdvForm.patientId);
      
      const rdvData = {
        ...rdvForm,
        date: today,
        structureId: auxiliaireData.structureId,
        medecinNom: medecin ? `Dr. ${medecin.nom}` : "",
        medecinSpecialite: medecin ? medecin.specialite : "",
        patientNom: patient ? ` ${patient.nom}` : "",
        patientTelephone: patient ? patient.telephone : "",
        patientEmail: patient ? patient.email : "",
        createdAt: new Date(),
        createdBy: {
          id: auth.currentUser.uid,
          type: "auxiliaire",
          nom: `${auxiliaireData.prenom} ${auxiliaireData.nom}`
        },
        priorite: "normal" // Les rendez-vous ajoutés par des auxiliaires ont une priorité normale
      };
      
      const docRef = await addDoc(collection(db, "rendezvous"), rdvData);
      
      // Ajouter le nouveau rendez-vous à la liste locale
      setRendezvous([...rendezvous, { id: docRef.id, ...rdvData }]);
      
      // Envoyer une notification au patient si configuré
      if (patient && patient.telephone) {
        try {
          const notificationResult = await sendAppointmentNotification(
            {
              ...rdvData,
              id: docRef.id,
            },
            "confirmation"
          );
          
          if (!notificationResult.success) {
            console.warn("Impossible d'envoyer la notification WhatsApp:", notificationResult.error);
          }
        } catch (notifError) {
          console.error("Erreur lors de l'envoi de la notification:", notifError);
        }
      }
      
      setMessage("Rendez-vous ajouté avec succès.");
      resetRdvForm();
      setShowRendezVousForm(false);
      setLoading(false);
      
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(`Erreur: ${error.message}`);
      setLoading(false);
    }
  };
  
// Fonction pour vérifier si l'auxiliaire est dans ses heures de travail
const verifierHoraireTravail = () => {
  if (!auxiliaireData || !auxiliaireData.horaires) {
    console.log("Aucun horaire défini, accès autorisé par défaut");
    return true; // Si aucun horaire n'est défini, permettre l'accès
  }
  
  const maintenant = new Date();
  const joursMap = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
  const jourSemaine = joursMap[maintenant.getDay()];
  const jourSemaineCapitalized = jourSemaine.charAt(0).toUpperCase() + jourSemaine.slice(1);
  
  console.log("Jour actuel:", jourSemaineCapitalized);
  console.log("Heure actuelle:", maintenant.getHours() + ":" + maintenant.getMinutes());
  console.log("Horaires disponibles:", auxiliaireData.horaires);
  
  // Vérifier si l'auxiliaire travaille aujourd'hui
  let horaireAujourdhui = null;
  
  // Vérifier si les horaires sont stockés sous forme de tableau (format correct)
  if (Array.isArray(auxiliaireData.horaires)) {
    // Rechercher le jour dans les horaires (avec différentes variantes de casse)
    horaireAujourdhui = auxiliaireData.horaires.find(
      horaire => {
        if (!horaire.jour) return false;
        const jourHoraire = horaire.jour.toLowerCase();
        return jourHoraire === jourSemaine || jourHoraire === jourSemaineCapitalized.toLowerCase();
      }
    );
    
    console.log("Horaire trouvé pour aujourd'hui:", horaireAujourdhui);
    
    if (!horaireAujourdhui) {
      // L'auxiliaire ne travaille pas aujourd'hui
      console.log("L'auxiliaire ne travaille pas aujourd'hui");
      setHoraireInfo({
        jour: jourSemaineCapitalized,
        debut: "",
        fin: ""
      });
      return false;
    }
    
    // Convertir l'heure actuelle en minutes depuis minuit
    const heureActuelle = maintenant.getHours();
    const minutesActuelles = maintenant.getMinutes();
    const minutesDepuisMinuit = heureActuelle * 60 + minutesActuelles;
    
    // Convertir les heures de début et de fin en minutes depuis minuit
    const [heureDebutH, heureDebutM] = (horaireAujourdhui.heureDebut || "08:00").split(':').map(Number);
    const [heureFinH, heureFinM] = (horaireAujourdhui.heureFin || "18:00").split(':').map(Number);
    
    const minutesDebut = heureDebutH * 60 + heureDebutM;
    const minutesFin = heureFinH * 60 + heureFinM;
    
    // Stocker les informations d'horaire pour l'affichage
    setHoraireInfo({
      jour: jourSemaineCapitalized,
      debut: horaireAujourdhui.heureDebut || "08:00",
      fin: horaireAujourdhui.heureFin || "18:00"
    });
    
    console.log("Heure actuelle (minutes):", minutesDepuisMinuit);
    console.log("Heure début (minutes):", minutesDebut);
    console.log("Heure fin (minutes):", minutesFin);
    
    // Vérifier si l'heure actuelle est dans la plage de travail
    const estDansPlage = minutesDepuisMinuit >= minutesDebut && minutesDepuisMinuit <= minutesFin;
    console.log("Est dans la plage horaire:", estDansPlage);
    return estDansPlage;
  } 
  // Si les horaires sont stockés sous forme d'objet (ancien format)
  else if (typeof auxiliaireData.horaires === 'object') {
    horaireAujourdhui = auxiliaireData.horaires[jourSemaine];
    
    if (!horaireAujourdhui || !horaireAujourdhui.actif) {
      // L'auxiliaire ne travaille pas aujourd'hui
      console.log("L'auxiliaire ne travaille pas aujourd'hui (format objet)");
      setHoraireInfo({
        jour: jourSemaineCapitalized,
        debut: "",
        fin: ""
      });
      return false;
    }
    
    // Convertir l'heure actuelle en minutes depuis minuit
    const heureActuelle = maintenant.getHours();
    const minutesActuelles = maintenant.getMinutes();
    const minutesDepuisMinuit = heureActuelle * 60 + minutesActuelles;
    
    // Convertir les heures de début et de fin en minutes depuis minuit
    const [heureDebutH, heureDebutM] = (horaireAujourdhui.debut || "08:00").split(':').map(Number);
    const [heureFinH, heureFinM] = (horaireAujourdhui.fin || "18:00").split(':').map(Number);
    
    const minutesDebut = heureDebutH * 60 + heureDebutM;
    const minutesFin = heureFinH * 60 + heureFinM;
    
    // Stocker les informations d'horaire pour l'affichage
    setHoraireInfo({
      jour: jourSemaineCapitalized,
      debut: horaireAujourdhui.debut || "08:00",
      fin: horaireAujourdhui.fin || "18:00"
    });
    
    console.log("Heure actuelle (minutes):", minutesDepuisMinuit);
    console.log("Heure début (minutes):", minutesDebut);
    console.log("Heure fin (minutes):", minutesFin);
    
    // Vérifier si l'heure actuelle est dans la plage de travail
    const estDansPlage = minutesDepuisMinuit >= minutesDebut && minutesDepuisMinuit <= minutesFin;
    console.log("Est dans la plage horaire (format objet):", estDansPlage);
    return estDansPlage;
  }
  
  // Si le format des horaires n'est pas reconnu, permettre l'accès par défaut
  console.log("Format d'horaires non reconnu, accès autorisé par défaut");
  return true;
};



// Vérifier les horaires de travail au chargement et toutes les minutes
useEffect(() => {
  const checkWorkingHours = () => {
    if (auxiliaireData) {
      const maintenant = new Date();
      const joursMap = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
      const jourSemaine = joursMap[maintenant.getDay()];
      
      // Trouver l'horaire pour aujourd'hui
      let horaireAujourdhui = null;
      let heureFin = "";
      let minutesFin = 0;
      
      // Vérifier le format des horaires (tableau ou objet)
      if (Array.isArray(auxiliaireData.horaires)) {
        horaireAujourdhui = auxiliaireData.horaires.find(
          horaire => {
            if (!horaire.jour) return false;
            const jourHoraire = horaire.jour.toLowerCase();
            return jourHoraire === jourSemaine || jourHoraire === jourSemaine.charAt(0).toUpperCase() + jourSemaine.slice(1).toLowerCase();
          }
        );
        
        if (horaireAujourdhui) {
          heureFin = horaireAujourdhui.heureFin || "18:00";
          const [heureFinH, heureFinM] = heureFin.split(':').map(Number);
          minutesFin = heureFinH * 60 + heureFinM;
        }
      } else if (typeof auxiliaireData.horaires === 'object') {
        horaireAujourdhui = auxiliaireData.horaires[jourSemaine];
        
        if (horaireAujourdhui && horaireAujourdhui.actif) {
          heureFin = horaireAujourdhui.fin || "18:00";
          const [heureFinH, heureFinM] = heureFin.split(':').map(Number);
          minutesFin = heureFinH * 60 + heureFinM;
        }
      }
      
      // Si on a trouvé un horaire pour aujourd'hui
      if (horaireAujourdhui) {
        // Calculer l'heure actuelle en minutes
        const heureActuelle = maintenant.getHours();
        const minutesActuelles = maintenant.getMinutes();
        const secondesActuelles = maintenant.getSeconds();
        const minutesDepuisMinuit = heureActuelle * 60 + minutesActuelles;
        
        // Vérifier si l'auxiliaire est dans ses heures de travail
        const estDansHoraireTravail = verifierHoraireTravail();
        
        // Si l'auxiliaire n'est plus dans ses heures de travail, le déconnecter
        if (!estDansHoraireTravail) {
          setHorsHoraireTravail(true);
          setCountdownSeconds(60); // Démarrer le compte à rebours à 60 secondes
        } 
        // Si l'auxiliaire est dans ses heures de travail mais qu'il reste moins de 3 minutes avant la fin
        else if (minutesFin - minutesDepuisMinuit <= 3) {
          // Calculer le temps restant précis
          const minutesRestantes = minutesFin - minutesDepuisMinuit;
          const secondesRestantes = 60 - secondesActuelles;
          
          if (minutesRestantes === 0) {
            // Moins d'une minute restante, afficher le compte à rebours en secondes
            setMessage(`Attention : Votre session de travail se termine bientôt.`);
            setCountdownSeconds(secondesRestantes);
          } else {
            // Entre 1 et 3 minutes restantes
            setMessage(`Attention : Votre session de travail se termine dans ${minutesRestantes} minute(s). Veuillez terminer vos tâches en cours.`);
            setCountdownSeconds(null);
          }
        } else {
          // Plus de 3 minutes restantes, pas d'avertissement
          setCountdownSeconds(null);
        }
      }
    }
  };
  
  // Vérifier immédiatement
  checkWorkingHours();
  
  // Vérifier toutes les minutes
  const intervalId = setInterval(checkWorkingHours, 60000); // Vérification toutes les minutes
  
  return () => clearInterval(intervalId);
}, [auxiliaireData]);



  // Effet pour gérer le compte à rebours par secondes
useEffect(() => {
  let countdownInterval;
  
  if (countdownSeconds !== null) {
    countdownInterval = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev <= 1) {
          // Quand le compte à rebours atteint 0, déconnecter l'utilisateur
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }
  
  return () => {
    if (countdownInterval) clearInterval(countdownInterval);
  };
}, [countdownSeconds]);


const CountdownDisplay = styled(motion.div)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  width: 150px;
  height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(220, 38, 38, 0.3);
  z-index: 2001;
  color: #dc2626;
  
  .countdown-number {
    font-size: 3.5rem;
    font-weight: bold;
    line-height: 1;
  }
  
  .countdown-text {
    font-size: 1rem;
    margin-top: 0.5rem;
  }
  
  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    
    .countdown-number {
      font-size: 2.5rem;
    }
    
    .countdown-text {
      font-size: 0.9rem;
    }
  }
`;


  // Fonction pour se déconnecter
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/auth");
  };
  
  // Filtrer les patients selon le terme de recherche
  const filteredPatients = patients.filter(patient => {
    if (!searchTerm) return true;
    const fullName = ` ${patient.nom}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) || 
           patient.telephone.includes(searchTerm) ||
           (patient.email && patient.email.toLowerCase().includes(searchTerm.toLowerCase()));
  });
  
  // Fonction pour ouvrir le formulaire d'ajout via le bouton flottant mobile
  const handleFloatingActionButton = () => {
    if (activeTab === "rendezVous") {
      setShowRendezVousForm(true);
    } else if (activeTab === "patients") {
      setShowPatientForm(true);
    }
  };

  // Composant pour l'alerte de fin de session imminente
const SessionEndingAlert = styled(motion.div)`
position: fixed;
bottom: 100px;
left: 50%;
transform: translateX(-50%);
background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
border-left: 5px solid #ffc107;
color: #856404;
padding: 1rem 1.5rem;
border-radius: 12px;
box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
z-index: 1000;
display: flex;
align-items: center;
max-width: 90%;

.icon {
  margin-right: 1rem;
  font-size: 1.5rem;
  color: #ffc107;
}

@media (max-width: 768px) {
  bottom: 80px;
  padding: 0.75rem 1rem;
  
  .icon {
    font-size: 1.25rem;
  }
}
`;

  
  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center vh-100">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <motion.div
            animate={{ 
              rotate: [0, 360],
            }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity,
              ease: "linear" 
            }}
          >
            <FontAwesomeIcon icon={faSpinner} className="text-primary" size="3x" />
          </motion.div>
          <motion.h5 
            className="text-muted mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Chargement de votre espace...
          </motion.h5>
        </motion.div>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container className="mt-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Alert variant="danger" className="p-4 text-center rounded-4 shadow">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring",
                stiffness: 300,
                damping: 15
              }}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} className="mb-3" size="3x" />
            </motion.div>
            <h4 className="mt-3">Accès non autorisé</h4>
            <p className="mb-0">{error}</p>
          </Alert>
        </motion.div>
      </Container>
    );
  }
  
  return (
    <>
      <GlobalStyles />
      <PageWrapper>
          {/* Compte à rebours de déconnexion */}
<AnimatePresence>
  {countdownSeconds !== null && countdownSeconds > 0 && (
    <CountdownDisplay
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.5, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div 
        className="countdown-number"
        key={countdownSeconds}
        initial={{ scale: 1.2 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {countdownSeconds}
      </motion.div>
      <div className="countdown-text">secondes</div>
    </CountdownDisplay>
  )}
</AnimatePresence>

{/* Alerte de fin de session imminente */}
<AnimatePresence>
  {message && message.includes("Attention : Votre session") && (
    <SessionEndingAlert
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div 
        className="icon"
        animate={{ 
          rotate: [0, 15, -15, 15, 0],
        }}
        transition={{ 
          duration: 1,
          repeat: Infinity,
          repeatDelay: 2
        }}
      >
        <FontAwesomeIcon icon={faClock} />
      </motion.div>
      <div>{message}</div>
    </SessionEndingAlert>
  )}
</AnimatePresence>
        
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <MobileMenuOverlay 
                initial="closed"
                animate="open"
                exit="closed"
                variants={overlayVariants}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <MobileMenu
                initial="closed"
                animate="open"
                exit="closed"
                variants={mobileMenuVariants}
              >
                <MobileMenuHeader>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="me-3" />
                  </motion.div>
                  <h5 className="mb-0">Menu</h5>
                </MobileMenuHeader>
                
                <MobileMenuItem 
                  className={activeTab === "rendezVous" ? "active" : ""}
                  onClick={() => {
                    setActiveTab("rendezVous");
                    setIsMobileMenuOpen(false);
                  }}
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FontAwesomeIcon icon={faCalendarDay} />
                  <span className="ms-2">Rendez-vous</span>
                </MobileMenuItem>
                
                <MobileMenuItem 
                  className={activeTab === "patients" ? "active" : ""}
                  onClick={() => {
                    setActiveTab("patients");
                    setIsMobileMenuOpen(false);
                  }}
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FontAwesomeIcon icon={faUser} />
                  <span className="ms-2">Patients</span>
                </MobileMenuItem>
                
                <div style={{ marginTop: "auto" }}>
                  <MobileMenuItem 
                    onClick={handleLogout}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    <span className="ms-2">Déconnexion</span>
                  </MobileMenuItem>
                </div>
              </MobileMenu>
            </>
          )}
        </AnimatePresence>
        
        <Container>
          <DashboardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <MobileMenuButton
                  onClick={() => setIsMobileMenuOpen(true)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FontAwesomeIcon icon={faBars} />
                </MobileMenuButton>
                
                <motion.div
                  initial={{ rotate: -10, scale: 0.9 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 300,
                    damping: 15
                  }}
                  className="me-3 d-none d-md-block"
                >
                  <IconWrapper
                    whileHover={{ 
                      scale: 1.1,
                      rotate: 5,
                      background: "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%)"
                    }}
                  >
                    <FontAwesomeIcon icon={faUserMd} size="lg" />
                  </IconWrapper>
                </motion.div>
                <div>
                  <h4 className="mb-0 fw-bold d-none d-md-block">Espace Auxiliaire</h4>
                  <h5 className="mb-0 fw-bold d-md-none">Espace Auxiliaire</h5>
                  <p className="text-muted mb-0 d-none d-md-block">
                    {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  <p className="text-muted mb-0 d-md-none" style={{ fontSize: '0.8rem' }}>
                    {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                  </p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="d-none d-md-block"
              >
                <StyledButton 
                  className="danger"
                  onClick={handleLogout}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                  Déconnexion
                </StyledButton>
              </motion.div>
              <NotificationBell
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                animate={{ 
                  rotate: [0, 10, -10, 10, 0],
                }}
                transition={{ 
                  duration: 0.5,
                  repeat: 2,
                  repeatDelay: 5
                }}
                className="d-md-none"
              >
                <FontAwesomeIcon icon={faBell} />
              </NotificationBell>
            </div>
          </DashboardHeader>
          
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <WelcomeBanner variants={itemVariants}>
              <Row className="align-items-center">
                <Col>
                  <motion.h2 
                    className="fw-bold mb-2 d-none d-md-block"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    Bienvenue, {auxiliaireData.prenom} {auxiliaireData.nom}
                  </motion.h2>
                  <motion.h4 
                    className="fw-bold mb-1 d-md-none"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    Bienvenue, {auxiliaireData.prenom}
                  </motion.h4>
                  <motion.p 
                    className="mb-0 fs-5 d-none d-md-block"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    Vous êtes connecté(e) en tant que {auxiliaireData.role || 'auxiliaire médical'}
                  </motion.p>
                  <motion.p 
                    className="mb-0 d-md-none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    style={{ fontSize: '0.9rem' }}
                  >
                    {auxiliaireData.role || 'Auxiliaire médical'}
                  </motion.p>
                </Col>
                <Col xs="auto" className="d-none d-md-block">
                  <NotificationBell
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    animate={{ 
                      rotate: [0, 10, -10, 10, 0],
                    }}
                    transition={{ 
                      duration: 0.5,
                      repeat: 2,
                      repeatDelay: 5
                    }}
                  >
                    <FontAwesomeIcon icon={faBell} size="2x" />
                  </NotificationBell>
                </Col>
              </Row>
            </WelcomeBanner>
            
            {message && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert 
                    variant={message.includes("Erreur") ? "danger" : "success"} 
                    dismissible
                    onClose={() => setMessage("")}
                    className="mb-4 shadow rounded-4"
                  >
                    <div className="d-flex align-items-center">
                      <motion.div
                        initial={{ scale: 0.5 }}
                        animate={{ scale: 1 }}
                        transition={{ 
                          type: "spring",
                          stiffness: 300,
                          damping: 10
                        }}
                        className="me-3"
                      >
                        <FontAwesomeIcon 
                          icon={message.includes("Erreur") ? faExclamationTriangle : faCheck} 
                          className="me-3"
                          size="lg"
                        />
                      </motion.div>
                      <div>{message}</div>
                    </div>
                  </Alert>
                </motion.div>
              </AnimatePresence>
            )}
            
            <StyledTabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4"
              variant="pills"
            >
              <Tab eventKey="rendezVous" title={
                <motion.div 
                  className="d-flex align-items-center"
                  whileHover={{ scale: 1.05 }}
                >
                  <FontAwesomeIcon icon={faCalendarDay} className="me-2" />
                  <span className="d-none d-sm-inline">Rendez-vous du jour</span>
                  <span className="d-sm-none">RDV</span>
                  <Badge bg="primary" pill className="ms-2">{rendezvous.length}</Badge>
                </motion.div>
              } />
              <Tab eventKey="patients" title={
                <motion.div 
                  className="d-flex align-items-center"
                  whileHover={{ scale: 1.05 }}
                >
                  <FontAwesomeIcon icon={faUser} className="me-2" />
                  Patients
                  <Badge bg="primary" pill className="ms-2">{patients.length}</Badge>
                </motion.div>
              } />
                <Tab eventKey="qrCode" title={
    <motion.div 
      className="d-flex align-items-center"
      whileHover={{ scale: 1.05 }}
    >
      <FontAwesomeIcon icon={faQrcode} className="me-2" />
      <span className="d-none d-sm-inline">Code QR</span>
      <span className="d-sm-none">QR</span>
    </motion.div>
  } />
            </StyledTabs>
            
            {activeTab === "rendezVous" && (
              <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <motion.h4 
                    className="mb-0 d-none d-md-block"
                    whileHover={{ x: 5 }}
                  >
                    <IconWrapper
                      whileHover={{ 
                        scale: 1.1,
                        rotate: 5,
                        background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                      }}
                      className="me-2 d-inline-flex"
                    >
                      <FontAwesomeIcon icon={faCalendarDay} />
                    </IconWrapper>
                    Rendez-vous du {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                  </motion.h4>
                  <motion.h5 
                    className="mb-0 d-md-none"
                    whileHover={{ x: 5 }}
                  >
                    <IconWrapper
                      whileHover={{ 
                        scale: 1.1,
                        rotate: 5,
                        background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                      }}
                      className="me-2 d-inline-flex"
                    >
                      <FontAwesomeIcon icon={faCalendarDay} />
                    </IconWrapper>
                    Rendez-vous du jour
                  </motion.h5>
                  {auxiliaireData.permissions.ajouterRendezVous && !horsHoraireTravail && (
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="d-none d-md-block"
                    >
                      <StyledButton 
                        className="primary"
                        onClick={() => setShowRendezVousForm(!showRendezVousForm)}
                      >
                        <FontAwesomeIcon icon={faCalendarPlus} className="me-2" />
                        {showRendezVousForm ? "Annuler" : "Ajouter un rendez-vous"}
                      </StyledButton>
                    </motion.div>
                  )}
                </div>
                
                <AnimatePresence>
                  {showRendezVousForm && !horsHoraireTravail && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <StyledCard className="mb-4">
                        <CardHeader>
                          <h5 className="mb-0 fw-bold">
                            <FontAwesomeIcon icon={faCalendarPlus} className="me-2 text-primary" />
                            Ajouter un rendez-vous pour aujourd'hui
                          </h5>
                        </CardHeader>
                        <CardBody>
                          <Form onSubmit={handleAddRendezVous}>
                            <Row>
                              <Col md={6} className="mb-4">
                                <Form.Group>
                                  <Form.Label className="fw-bold">Médecin</Form.Label>
                                  <Form.Select
                                    name="medecinId"
                                    value={rdvForm.medecinId}
                                    onChange={(e) => handleMedecinChange(e.target.value)}
                                    required
                                    className="rounded-pill py-2 shadow-sm"
                                  >
                                    <option value="">Sélectionner un médecin</option>
                                    {medecins.map((medecin) => (
                                      <option key={medecin.id} value={medecin.id}>
                                        Dr.  {medecin.nom} - {medecin.specialite}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              <Col md={6} className="mb-4">
                                <Form.Group>
                                  <Form.Label className="fw-bold">Patient</Form.Label>
                                  <Form.Select
                                    name="patientId"
                                    value={rdvForm.patientId}
                                    onChange={handleRdvChange}
                                    required
                                    className="rounded-pill py-2 shadow-sm"
                                  >
                                    <option value="">Sélectionner un patient</option>
                                    {patients.map((patient) => (
                                      <option key={patient.id} value={patient.id}>
                                         {patient.nom}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                            </Row>
                            
                            <Form.Group className="mb-4">
                              <Form.Label className="fw-bold">Créneau horaire</Form.Label>
                              {rdvForm.medecinId ? (
                                creneauxDisponibles.length > 0 ? (
                                  <TimeSlotGrid>
                                    {creneauxDisponibles.map((creneau) => (
                                      <TimeSlot
                                        key={creneau.heure}
                                        className={`${creneau.statut} ${
                                          rdvForm.heure === creneau.heure ? "selected" : ""
                                        }`}
                                        onClick={() => {
                                          if (creneau.estDisponible) {
                                            setRdvForm({
                                              ...rdvForm,
                                              heure: creneau.heure,
                                            });
                                          }
                                        }}
                                        whileHover={creneau.estDisponible ? { scale: 1.05, y: -3 } : {}}
                                        whileTap={creneau.estDisponible ? { scale: 0.95 } : {}}
                                        transition={{ duration: 0.2 }}
                                      >
                                        {creneau.heure}
                                        {creneau.statut === "partiel" && (
                                          <div className="slot-info">
                                            {creneau.nombreRdv}/3 occupé
                                            {creneau.nombreRdv > 1 ? "s" : ""}
                                          </div>
                                        )}
                                        {creneau.statut === "complet" && (
                                          <div className="slot-info">Complet</div>
                                        )}
                                      </TimeSlot>
                                    ))}
                                  </TimeSlotGrid>
                                ) : (
                                  <Alert variant="warning" className="rounded-4 border-0 shadow-sm">
                                    <div className="d-flex align-items-center">
                                      <motion.div
                                        animate={{ 
                                          rotate: [0, 10, -10, 10, 0],
                                        }}
                                        transition={{ 
                                          duration: 0.5,
                                          repeat: 1,
                                          repeatDelay: 1
                                        }}
                                        className="me-3"
                                      >
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="fs-4" />
                                      </motion.div>
                                      <div>
                                        <strong>Aucun créneau disponible</strong>
                                        <p className="mb-0">Ce médecin n'a pas de disponibilité pour aujourd'hui.</p>
                                      </div>
                                    </div>
                                  </Alert>
                                )
                              ) : (
                                <Alert variant="info" className="rounded-4 border-0 shadow-sm">
                                  <div className="d-flex align-items-center">
                                    <motion.div
                                      animate={{ y: [0, -5, 0] }}
                                      transition={{ 
                                        duration: 1,
                                        repeat: Infinity,
                                        repeatType: "reverse"
                                      }}
                                      className="me-3"
                                    >
                                      <FontAwesomeIcon icon={faCalendarAlt} className="fs-4" />
                                    </motion.div>
                                    <div>
                                      <strong>Sélectionnez un médecin</strong>
                                      <p className="mb-0">Les créneaux disponibles s'afficheront ici.</p>
                                    </div>
                                  </div>
                                </Alert>
                              )}
                            </Form.Group>
                            
                            <Form.Group className="mb-4">
                              <Form.Label className="fw-bold">Motif</Form.Label>
                              <Form.Control
                                as="textarea"
                                name="motif"
                                value={rdvForm.motif}
                                onChange={handleRdvChange}
                                rows={3}
                                className="rounded-3 shadow-sm"
                                placeholder="Décrivez brièvement le motif de la consultation..."
                              />
                            </Form.Group>
                            
                            <div className="d-flex justify-content-end gap-3 flex-wrap">
                              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="w-100 w-md-auto">
                                <StyledButton 
                                  type="button"
                                  className="secondary mobile-full"
                                  onClick={() => {
                                    resetRdvForm();
                                    setShowRendezVousForm(false);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                                  Annuler
                                </StyledButton>
                              </motion.div>
                              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="w-100 w-md-auto">
                                <StyledButton 
                                  type="submit"
                                  className="primary mobile-full"
                                  disabled={!rdvForm.medecinId || !rdvForm.patientId || !rdvForm.heure}
                                >
                                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                                  Confirmer le rendez-vous
                                </StyledButton>
                              </motion.div>
                            </div>
                          </Form>
                        </CardBody>
                      </StyledCard>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <StyledCard>
                  <CardHeader>
                    <h5 className="mb-0 fw-bold">
                      <FontAwesomeIcon icon={faClipboardList} className="me-2 text-primary" />
                      Planning des rendez-vous
                    </h5>
                    <Badge 
                      bg={rendezvous.length > 0 ? "primary" : "secondary"} 
                      pill
                      className="px-3 py-2"
                    >
                      {rendezvous.length} rendez-vous
                    </Badge>
                  </CardHeader>
                  <CardBody>
                    {rendezvous.length > 0 ? (
                      <>
                        {/* Version desktop du tableau */}
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5 }}
                          className="d-none d-md-block"
                        >
                          <StyledTable>
                            <thead>
                              <tr>
                                <th>Heure</th>
                                <th>Médecin</th>
                                <th>Patient</th>
                                <th>Motif</th>
                                <th>Statut</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rendezvous
                                .sort((a, b) => a.heure.localeCompare(b.heure))
                                .map((rdv, index) => (
                                  <motion.tr 
                                    key={rdv.id} 
                                    className={`status-${rdv.statut}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05, duration: 0.3 }}
                                    whileHover={{ 
                                      backgroundColor: "#f8f9fa",
                                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)"
                                    }}
                                  >
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <IconWrapper
                                          whileHover={{ 
                                            scale: 1.1,
                                            rotate: 5
                                          }}
                                          className="me-2"
                                        >
                                          <FontAwesomeIcon icon={faClock} />
                                        </IconWrapper>
                                        <strong>{rdv.heure}</strong>
                                      </div>
                                    </td>
                                    <td>{rdv.medecinNom}</td>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <IconWrapper
                                          whileHover={{ 
                                            scale: 1.1,
                                            rotate: 5
                                          }}
                                          className="me-2"
                                        >
                                          <FontAwesomeIcon icon={faUser} />
                                        </IconWrapper>
                                        {rdv.patientNom}
                                      </div>
                                    </td>
                                    <td>
                                      <div style={{ maxWidth: "200px" }} className="text-truncate">
                                        {rdv.motif || "-"}
                                      </div>
                                    </td>
                                    <td>
                                      <StatusBadge className={`status-badge-${rdv.statut}`}>
                                        {rdv.statut}
                                      </StatusBadge>
                                    </td>
                                  </motion.tr>
                                ))}
                            </tbody>
                          </StyledTable>
                        </motion.div>
                        
                        {/* Version mobile des cartes de rendez-vous */}
                        <MobileCardList>
                          {rendezvous
                            .sort((a, b) => a.heure.localeCompare(b.heure))
                            .map((rdv, index) => (
                              <MobileCard 
                                key={rdv.id}
                                className={`status-${rdv.statut}`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05, duration: 0.3 }}
                                whileHover={{ y: -3 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <MobileCardHeader>
                                  <div className="d-flex align-items-center">
                                    <IconWrapper className="me-2">
                                      <FontAwesomeIcon icon={faClock} />
                                    </IconWrapper>
                                    <strong>{rdv.heure}</strong>
                                  </div>
                                  <StatusBadge className={`status-badge-${rdv.statut}`}>
                                    {rdv.statut}
                                  </StatusBadge>
                                </MobileCardHeader>
                                <MobileCardBody>
                                  <MobileCardItem>
                                    <div className="label">Médecin:</div>
                                    <div className="value">{rdv.medecinNom}</div>
                                  </MobileCardItem>
                                  <MobileCardItem>
                                    <div className="label">Patient:</div>
                                    <div className="value">{rdv.patientNom}</div>
                                  </MobileCardItem>
                                  {rdv.motif && (
                                    <MobileCardItem>
                                      <div className="label">Motif:</div>
                                      <div className="value">{rdv.motif}</div>
                                    </MobileCardItem>
                                  )}
                                </MobileCardBody>
                              </MobileCard>
                            ))}
                        </MobileCardList>
                      </>
                    ) : (
                      <EmptyState>
                        <motion.div
                          animate={{ 
                            y: [0, -10, 0],
                            opacity: [0.7, 1, 0.7]
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity
                          }}
                        >
                          <FontAwesomeIcon icon={faCalendarDay} size="3x" />
                        </motion.div>
                        <h5>Aucun rendez-vous aujourd'hui</h5>
                        {auxiliaireData.permissions.ajouterRendezVous && !horsHoraireTravail && (
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="d-none d-md-block"
                          >
                            <StyledButton 
                              className="primary"
                              onClick={() => setShowRendezVousForm(true)}
                            >
                              <FontAwesomeIcon icon={faCalendarPlus} className="me-2" />
                              Ajouter un rendez-vous
                            </StyledButton>
                          </motion.div>
                        )}
                      </EmptyState>
                    )}
                  </CardBody>
                </StyledCard>
              </motion.div>
            )}
            
            {activeTab === "patients" && (
              <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <motion.h4 
                    className="mb-0 d-none d-md-block"
                    whileHover={{ x: 5 }}
                  >
                    <IconWrapper
                      whileHover={{ 
                        scale: 1.1,
                        rotate: 5,
                        background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                      }}
                      className="me-2 d-inline-flex"
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </IconWrapper>
                    Liste des patients
                  </motion.h4>
                  <motion.h5 
                    className="mb-0 d-md-none"
                    whileHover={{ x: 5 }}
                  >
                    <IconWrapper
                      whileHover={{ 
                        scale: 1.1,
                        rotate: 5,
                        background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                      }}
                      className="me-2 d-inline-flex"
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </IconWrapper>
                    Liste des patients
                  </motion.h5>
                  {auxiliaireData.permissions.ajouterPatients && !horsHoraireTravail && (
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="d-none d-md-block"
                    >
                      <StyledButton 
                        className="primary"
                        onClick={() => setShowPatientForm(!showPatientForm)}
                      >
                        <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                        {showPatientForm ? "Annuler" : "Ajouter un patient"}
                      </StyledButton>
                    </motion.div>
                  )}
                </div>
                
                <AnimatePresence>
                  {showPatientForm && !horsHoraireTravail && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <StyledCard className="mb-4">
                        <CardHeader>
                          <h5 className="mb-0 fw-bold">
                            <FontAwesomeIcon icon={faUserPlus} className="me-2 text-primary" />
                            Ajouter un patient
                          </h5>
                        </CardHeader>
                        <CardBody>
                          <Form onSubmit={handleAddPatient}>
                            <Row>
                              <Col md={6} className="mb-4">
                                <Form.Group>
                                  <Form.Label className="fw-bold">Nom et prénom</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="nom"
                                    value={patientForm.nom}
                                    onChange={handlePatientChange}
                                    required
                                    className="rounded-pill py-2 shadow-sm"
                                    placeholder="Nom de famille"
                                  />
                                </Form.Group>
                              </Col>
                             
                            </Row>
                            
                            <Row>
                              <Col md={6} className="mb-4">
                                <Form.Group>
                                  <Form.Label className="fw-bold">Date de naissance</Form.Label>
                                  <Form.Control
                                    type="date"
                                    name="dateNaissance"
                                    value={patientForm.dateNaissance}
                                    onChange={handlePatientChange}
                                    required
                                    className="rounded-pill py-2 shadow-sm"
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6} className="mb-4">
                                <Form.Group>
                                  <Form.Label className="fw-bold">Téléphone</Form.Label>
                                  <Form.Control
                                    type="tel"
                                    name="telephone"
                                    value={patientForm.telephone}
                                    onChange={handlePatientChange}
                                    required
                                    className="rounded-pill py-2 shadow-sm"
                                    placeholder="Numéro de téléphone"
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                            
                            <Form.Group className="mb-4">
                              <Form.Label className="fw-bold">Email</Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={patientForm.email}
                                onChange={handlePatientChange}
                                className="rounded-pill py-2 shadow-sm"
                                placeholder="Adresse email"
                              />
                            </Form.Group>
                            
                            <div className="d-flex justify-content-end gap-3 flex-wrap">
                              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="w-100 w-md-auto">
                                <StyledButton 
                                  type="button"
                                  className="secondary mobile-full"
                                  onClick={() => {
                                    resetPatientForm();
                                    setShowPatientForm(false);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                                  Annuler
                                </StyledButton>
                              </motion.div>
                              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="w-100 w-md-auto">
                                <StyledButton 
                                  type="submit"
                                  className="primary mobile-full"
                                >
                                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                                  Ajouter le patient
                                </StyledButton>
                              </motion.div>
                            </div>
                          </Form>
                        </CardBody>
                      </StyledCard>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <StyledCard>
                  <CardHeader>
                    <h5 className="mb-0 fw-bold">
                    <FontAwesomeIcon icon={faClipboardList} className="me-2 text-primary" />
                      Répertoire des patients
                    </h5>
                    <Badge 
                      bg={patients.length > 0 ? "primary" : "secondary"} 
                      pill
                      className="px-3 py-2"
                    >
                      {patients.length} patients
                    </Badge>
                  </CardHeader>
                  <CardBody>
                    <SearchBox className="mb-4">
                      <FontAwesomeIcon icon={faSearch} />
                      <input
                        type="text"
                        placeholder="Rechercher un patient..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </SearchBox>
                    
                    {patients.length > 0 ? (
                      <>
                        {filteredPatients.length > 0 ? (
                          <motion.div 
                            className="row g-4"
                            initial="hidden"
                            animate="visible"
                            variants={containerVariants}
                          >
                            {filteredPatients.map((patient, index) => (
                              <motion.div 
                                key={patient.id} 
                                className="col-md-6 col-lg-4"
                                variants={itemVariants}
                                transition={{ delay: index * 0.05 }}
                              >
                                <MobilePatientCard
                                  whileHover={{ y: -3, boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)" }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  <MobilePatientHeader>
                                    <IconWrapper
                                      whileHover={{ 
                                        scale: 1.1,
                                        rotate: 5,
                                        background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                                      }}
                                      className="me-3"
                                    >
                                      <FontAwesomeIcon icon={faUser} />
                                    </IconWrapper>
                                    <MobilePatientDetails>
                                      <h6 className="mb-0 fw-bold">{patient.nom}</h6>
                                      <small className="text-muted">
                                        {patient.dateNaissance ? new Date(patient.dateNaissance).toLocaleDateString('fr-FR') : "Date de naissance non renseignée"}
                                      </small>
                                    </MobilePatientDetails>
                                  </MobilePatientHeader>
                                  
                                  <MobilePatientContact>
                                    <FontAwesomeIcon icon={faPhone} />
                                    <span>{patient.telephone}</span>
                                  </MobilePatientContact>
                                  
                                  {patient.email && (
                                    <MobilePatientContact>
                                      <FontAwesomeIcon icon={faEnvelope} />
                                      <span>{patient.email}</span>
                                    </MobilePatientContact>
                                  )}
                                </MobilePatientCard>
                              </motion.div>
                            ))}
                          </motion.div>
                        ) : (
                          <EmptyState>
                            <motion.div
                              animate={{ 
                                y: [0, -10, 0],
                                opacity: [0.7, 1, 0.7]
                              }}
                              transition={{ 
                                duration: 2,
                                repeat: Infinity
                              }}
                            >
                              <FontAwesomeIcon icon={faSearch} size="3x" />
                            </motion.div>
                            <h5>Aucun patient ne correspond à votre recherche</h5>
                          </EmptyState>
                        )}
                      </>
                    ) : (
                      <EmptyState>
                        <motion.div
                          animate={{ 
                            y: [0, -10, 0],
                            opacity: [0.7, 1, 0.7]
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity
                          }}
                        >
                          <FontAwesomeIcon icon={faUser} size="3x" />
                        </motion.div>
                        <h5>Aucun patient enregistré</h5>
                        {auxiliaireData.permissions.ajouterPatients && !horsHoraireTravail && (
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="d-none d-md-block"
                          >
                            <StyledButton 
                              className="primary"
                              onClick={() => setShowPatientForm(true)}
                            >
                              <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                              Ajouter un patient
                            </StyledButton>
                          </motion.div>
                        )}
                      </EmptyState>
                    )}
                  </CardBody>
                </StyledCard>
              </motion.div>
            )}

{activeTab === "qrCode" && (
  <motion.div
    variants={fadeInVariants}
    initial="hidden"
    animate="visible"
  >
    <div className="d-flex justify-content-between align-items-center mb-4">
      <motion.h4 
        className="mb-0 d-none d-md-block"
        whileHover={{ x: 5 }}
      >
        <IconWrapper
          whileHover={{ 
            scale: 1.1,
            rotate: 5,
            background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
          }}
          className="me-2 d-inline-flex"
        >
          <FontAwesomeIcon icon={faQrcode} />
        </IconWrapper>
        Générateur de Code QR
      </motion.h4>
      <motion.h5 
        className="mb-0 d-md-none"
        whileHover={{ x: 5 }}
      >
        <IconWrapper
          whileHover={{ 
            scale: 1.1,
            rotate: 5,
            background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
          }}
          className="me-2 d-inline-flex"
        >
          <FontAwesomeIcon icon={faQrcode} />
        </IconWrapper>
        Générateur de QR
      </motion.h5>
    </div>
    
    {!horsHoraireTravail ? (
      <QRCodeGenerator 
        auxiliaireData={{...auxiliaireData, id: auth.currentUser.uid}} 
          baseUrl = 'https://www.samdigitale.com/'

      />
    ) : (
      <StyledCard>
        <CardBody>
          <EmptyState>
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity
              }}
            >
              <FontAwesomeIcon icon={faClock} size="3x" />
            </motion.div>
            <h5>Génération de code QR indisponible</h5>
            <p>Vous ne pouvez générer des codes QR que pendant vos heures de travail.</p>
          </EmptyState>
        </CardBody>
      </StyledCard>
    )}
  </motion.div>
)}


          </motion.div>
        </Container>
        
        {/* Bouton d'action flottant pour mobile */}
        {(auxiliaireData.permissions.ajouterPatients || auxiliaireData.permissions.ajouterRendezVous) && !horsHoraireTravail && (
          <FloatingActionButton
            onClick={handleFloatingActionButton}
            whileHover={{ scale: 1.1, boxShadow: "0 8px 25px rgba(99, 102, 241, 0.4)" }}
            whileTap={{ scale: 0.9 }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ 
              type: "spring",
              stiffness: 300,
              damping: 15
            }}
          >
            <FontAwesomeIcon icon={activeTab === "rendezVous" ? faCalendarPlus : faUserPlus} />
          </FloatingActionButton>
        )}
        
        {/* Barre d'onglets mobile */}
        <MobileTabBar>
          <MobileTabItem 
            className={activeTab === "rendezVous" ? "active" : ""}
            onClick={() => setActiveTab("rendezVous")}
            whileTap={{ scale: 0.9 }}
          >
            <FontAwesomeIcon icon={faCalendarDay} />
            <span>RDV</span>
          </MobileTabItem>
          <MobileTabItem 
            className={activeTab === "patients" ? "active" : ""}
            onClick={() => setActiveTab("patients")}
            whileTap={{ scale: 0.9 }}
          >
            <FontAwesomeIcon icon={faUser} />
            <span>Patients</span>
          </MobileTabItem>
          <MobileTabItem 
    className={activeTab === "qrCode" ? "active" : ""}
    onClick={() => setActiveTab("qrCode")}
    whileTap={{ scale: 0.9 }}
  >
    <FontAwesomeIcon icon={faQrcode} />
    <span>QR</span>
  </MobileTabItem>
        </MobileTabBar>
      

      </PageWrapper>
    </>
  );
};

// Styles globaux
const GlobalStyles = createGlobalStyle`
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    background-color: #f8f9fa;
    color: #1f2937;
  }
  
  .form-control:focus, .form-select:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 0.25rem rgba(99, 102, 241, 0.25);
  }
  
  .form-label {
    margin-bottom: 0.5rem;
    color: #4b5563;
  }
`;

export default AuxiliaireDashboard;